import React, { useState, useEffect } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { Plus, X, Trash2, Edit, Save, Users, Eye, EyeOff, AlertCircle, Search } from 'lucide-react';
import './ManagersManagement.css';

const ManagersManagement = () => {
  const { authState } = useOktaAuth();
  const [managers, setManagers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [managerTeamVisibility, setManagerTeamVisibility] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedManager, setSelectedManager] = useState(null);
  const [selectedTeams, setSelectedTeams] = useState([]);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchManagers(),
        fetchTeams(),
        fetchManagerTeamVisibility()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchManagers = async () => {
    try {
      const response = await fetch('/api/employees?limit=1000');
      if (response.ok) {
        const data = await response.json();
        // Get unique managers from the employee list
        const uniqueManagers = [...new Set(data.map(emp => emp.manager))].filter(Boolean);
        const managersWithCounts = uniqueManagers.map(managerName => {
          const reportCount = data.filter(emp => emp.manager === managerName).length;
          const departments = [...new Set(data.filter(emp => emp.manager === managerName).map(emp => emp.department))];
          return {
            name: managerName,
            reportCount,
            departments
          };
        });
        setManagers(managersWithCounts);
      }
    } catch (error) {
      console.error('Error fetching managers:', error);
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await fetch('/api/teams');
      if (response.ok) {
        const data = await response.json();
        setTeams(data);
      }
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  };

  const fetchManagerTeamVisibility = async () => {
    try {
      const response = await fetch('/api/manager-team-visibility');
      if (response.ok) {
        const data = await response.json();
        // Convert array to object format: { managerName: [teamId1, teamId2] }
        const visibilityMap = {};
        data.forEach(item => {
          if (!visibilityMap[item.managerName]) {
            visibilityMap[item.managerName] = [];
          }
          visibilityMap[item.managerName].push(item.teamId);
        });
        setManagerTeamVisibility(visibilityMap);
      }
    } catch (error) {
      console.error('Error fetching manager team visibility:', error);
      // If endpoint doesn't exist yet, just continue
      setManagerTeamVisibility({});
    }
  };

  const openEditModal = (manager) => {
    setSelectedManager(manager);
    setSelectedTeams(managerTeamVisibility[manager.name] || []);
    setShowEditModal(true);
  };

  const handleTeamToggle = (teamId) => {
    setSelectedTeams(prev => {
      if (prev.includes(teamId)) {
        return prev.filter(id => id !== teamId);
      } else {
        return [...prev, teamId];
      }
    });
  };

  const handleSaveTeamVisibility = async () => {
    if (!selectedManager) return;

    try {
      const currentUser = authState?.idToken?.claims?.name || 'System User';
      
      const response = await fetch('/api/manager-team-visibility', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          managerName: selectedManager.name,
          teamIds: selectedTeams,
          updatedBy: currentUser
        }),
      });

      if (response.ok) {
        setManagerTeamVisibility(prev => ({
          ...prev,
          [selectedManager.name]: selectedTeams
        }));
        
        // Show success notification
        const notification = document.createElement('div');
        notification.className = 'success-notification';
        notification.innerHTML = `
          <div class="notification-content">
            <span class="notification-icon">✓</span>
            <span class="notification-message">Team visibility updated for ${selectedManager.name}</span>
          </div>
        `;
        document.body.appendChild(notification);
        
        setTimeout(() => {
          notification.classList.add('fade-out');
          setTimeout(() => document.body.removeChild(notification), 300);
        }, 3000);
        
        setShowEditModal(false);
        setSelectedManager(null);
        setSelectedTeams([]);
      } else {
        const error = await response.json();
        alert('Error updating team visibility: ' + error.error);
      }
    } catch (error) {
      console.error('Error saving team visibility:', error);
      alert('Error saving team visibility. Please try again.');
    }
  };

  const filteredManagers = managers.filter(manager => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      manager.name.toLowerCase().includes(query) ||
      manager.departments.some(dept => dept.toLowerCase().includes(query))
    );
  });

  if (loading) {
    return (
      <div className="managers-management">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '400px',
          flexDirection: 'column',
          gap: '16px'
        }}>
          <div className="loading-spinner"></div>
          <div>Loading managers data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="managers-management">
      <div className="managers-header">
        <div>
          <h2>Managers Management</h2>
          <p>Manage managers and their team visibility permissions</p>
        </div>
      </div>

      {/* Search Bar */}
      <div className="search-container" style={{ marginBottom: '24px' }}>
        <Search className="search-icon" size={20} />
        <input
          type="text"
          placeholder="Search managers or departments..."
          className="search-input"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
        {searchQuery && (
          <button 
            className="clear-search-btn"
            onClick={() => setSearchQuery('')}
            title="Clear search"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Edit Team Visibility Modal */}
      {showEditModal && selectedManager && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Edit Team Visibility for {selectedManager.name}</h3>
              <button className="close-btn" onClick={() => setShowEditModal(false)}>
                <X size={20} />
              </button>
            </div>
            
            <div className="modal-form">
              <div className="form-group">
                <label>Select Teams This Manager Can View</label>
                <p className="help-text">Choose which teams {selectedManager.name} should have visibility into.</p>
                
                <div className="teams-checklist">
                  {teams.map(team => (
                    <div key={team.TeamID} className="team-checkbox-item">
                      <label>
                        <input
                          type="checkbox"
                          checked={selectedTeams.includes(team.TeamID)}
                          onChange={() => handleTeamToggle(team.TeamID)}
                        />
                        <span className="team-label">
                          <strong>{team.Title}</strong>
                          {team.Manager && <span className="team-manager"> (Manager: {team.Manager})</span>}
                        </span>
                      </label>
                    </div>
                  ))}
                </div>

                {teams.length === 0 && (
                  <div className="empty-state-small">
                    <AlertCircle size={24} />
                    <p>No teams available. Create teams first in Teams Management.</p>
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowEditModal(false)} className="btn-cancel">
                  Cancel
                </button>
                <button type="button" onClick={handleSaveTeamVisibility} className="btn-save">
                  <Save size={16} />
                  Save Visibility Settings
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Managers List */}
      <div className="managers-list">
        {filteredManagers.length === 0 ? (
          <div className="empty-state">
            <AlertCircle size={48} className="empty-icon" />
            <h3>No managers found</h3>
            <p>
              {searchQuery 
                ? `No managers found matching "${searchQuery}"`
                : 'No managers were detected in the system. Managers are identified from employee records.'
              }
            </p>
          </div>
        ) : (
          <div className="managers-grid">
            {filteredManagers.map((manager, index) => {
              const visibleTeams = managerTeamVisibility[manager.name] || [];
              const visibleTeamNames = teams
                .filter(team => visibleTeams.includes(team.TeamID))
                .map(team => team.Title);

              return (
                <div key={index} className="manager-card">
                  <div className="manager-card-header">
                    <div className="manager-avatar">
                      {manager.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="manager-info">
                      <h3>{manager.name}</h3>
                      <div className="manager-stats">
                        <span className="stat">
                          <Users size={14} />
                          {manager.reportCount} direct report{manager.reportCount !== 1 ? 's' : ''}
                        </span>
                        <span className="stat">
                          <Eye size={14} />
                          {visibleTeams.length} team{visibleTeams.length !== 1 ? 's' : ''} visible
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="manager-card-body">
                    <div className="info-section">
                      <label>Departments</label>
                      <div className="tags-list">
                        {manager.departments.map((dept, idx) => (
                          <span key={idx} className="tag dept-tag">{dept}</span>
                        ))}
                      </div>
                    </div>

                    <div className="info-section">
                      <label>Team Visibility</label>
                      {visibleTeamNames.length > 0 ? (
                        <div className="tags-list">
                          {visibleTeamNames.map((teamName, idx) => (
                            <span key={idx} className="tag team-tag">{teamName}</span>
                          ))}
                        </div>
                      ) : (
                        <p className="no-data">No team visibility set</p>
                      )}
                    </div>
                  </div>

                  <div className="manager-card-footer">
                    <button 
                      className="btn-edit-visibility"
                      onClick={() => openEditModal(manager)}
                    >
                      <Eye size={16} />
                      Edit Team Visibility
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ManagersManagement;
